/**
 * 错误边界组件
 * 用于捕获和处理React组件错误
 */

import React from 'react';

/**
 * 错误边界类组件
 * React错误边界必须是类组件
 */
class ErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  /**
   * 捕获错误并更新状态
   * @param {Error} error - 错误对象
   * @returns {Object} 新的状态
   */
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error
    };
  }

  /**
   * 组件捕获错误时调用
   * @param {Error} error - 错误对象
   * @param {Object} errorInfo - 错误信息
   */
  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // 调用错误处理函数
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // 记录错误到控制台
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  /**
   * 重置错误状态
   */
  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      // 如果提供了自定义错误UI，则使用它
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.resetError);
      }

      // 否则使用默认错误UI
      return (
        <ErrorFallback 
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.resetError}
          showDetails={this.props.showDetails}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * 默认错误回退组件
 * @param {Object} props - 组件属性
 * @param {Error} props.error - 错误对象
 * @param {Object} props.errorInfo - 错误信息
 * @param {Function} props.onReset - 重置错误的回调函数
 * @param {boolean} props.showDetails - 是否显示错误详情
 */
function ErrorFallback({ error, errorInfo, onReset, showDetails = false }) {
  const [showErrorDetails, setShowErrorDetails] = React.useState(showDetails);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full bg-card border border-border rounded-lg shadow-lg p-6">
        {/* 错误图标 */}
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-destructive/10 rounded-full">
          <svg 
            className="w-8 h-8 text-destructive" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" 
            />
          </svg>
        </div>

        {/* 错误标题 */}
        <h1 className="text-xl font-semibold text-center text-foreground mb-2">
          出现了一些问题
        </h1>

        {/* 错误描述 */}
        <p className="text-sm text-muted-foreground text-center mb-6">
          应用程序遇到了意外错误。请尝试刷新页面或联系技术支持。
        </p>

        {/* 操作按钮 */}
        <div className="flex flex-col space-y-3">
          <button
            onClick={onReset}
            className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-medium"
          >
            重试
          </button>
          
          <button
            onClick={() => window.location.reload()}
            className="w-full px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
          >
            刷新页面
          </button>

          {/* 显示/隐藏错误详情按钮 */}
          <button
            onClick={() => setShowErrorDetails(!showErrorDetails)}
            className="w-full px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {showErrorDetails ? '隐藏' : '显示'}错误详情
          </button>
        </div>

        {/* 错误详情 */}
        {showErrorDetails && error && (
          <div className="mt-6 p-4 bg-muted rounded-md">
            <h3 className="text-sm font-medium text-foreground mb-2">错误详情:</h3>
            <div className="text-xs text-muted-foreground space-y-2">
              <div>
                <strong>错误消息:</strong>
                <pre className="mt-1 whitespace-pre-wrap break-words">
                  {error.message}
                </pre>
              </div>
              
              {error.stack && (
                <div>
                  <strong>错误堆栈:</strong>
                  <pre className="mt-1 whitespace-pre-wrap break-words text-xs">
                    {error.stack}
                  </pre>
                </div>
              )}
              
              {errorInfo && errorInfo.componentStack && (
                <div>
                  <strong>组件堆栈:</strong>
                  <pre className="mt-1 whitespace-pre-wrap break-words text-xs">
                    {errorInfo.componentStack}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * 错误边界Hook组件
 * @param {Object} props - 组件属性
 * @param {React.ReactNode} props.children - 子组件
 * @param {Function} props.fallback - 自定义错误回退组件
 * @param {Function} props.onError - 错误处理回调
 * @param {boolean} props.showDetails - 是否显示错误详情
 */
function ErrorBoundary({ children, fallback, onError, showDetails = false }) {
  /**
   * 处理错误
   * @param {Error} error - 错误对象
   * @param {Object} errorInfo - 错误信息
   */
  const handleErrorBoundary = (error, errorInfo) => {
    // 记录错误到控制台
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // 调用自定义错误处理
    if (onError) {
      onError(error, errorInfo);
    }
  };

  return (
    <ErrorBoundaryClass
      fallback={fallback}
      onError={handleErrorBoundary}
      showDetails={showDetails}
    >
      {children}
    </ErrorBoundaryClass>
  );
}

export default ErrorBoundary;
export { ErrorFallback };